import React, { useState, useEffect } from 'react';
import firebase from '../../config/firebaseConfig';
import { ADMIN_LIST } from '../../config/adminConfig'; // Ajuste le chemin selon ton dossier
import { connect } from 'react-redux';
import SidebarAdmin from './SidebarAdmin';
import FinancesPieChart from './FinancesPieChart';
import moment from 'moment';
import 'moment/locale/fr';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Modal } from 'react-bootstrap';
import { addTransaction, editTransaction, deleteTransaction } from '../../actions/financeActions';

moment.locale('fr');

const ComptePersonnel = ({ auth, profile, addTransaction, editTransaction, deleteTransaction }) => {

  // 👑 On extrait le rôle en toute sécurité
  const userRole = profile?.role || 'membre';

  const [transactions, setTransactions] = useState([]);
  const [form, setForm] = useState({ label: '', amount: '', type: 'debit', category: 'Aucune catégorie', date: moment().format('YYYY-MM-DD') });

  // 🚀 On initialise le mois à consulter sur le moment présent (Octobre 2026)
  const [moisSelectionne, setMoisSelectionne] = useState(moment());

  const [showEditModal, setShowEditModal] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);

  // ◀ FONCTION MOIS PRÉCÉDENT
  const moisPrecedent = () => {
    setMoisSelectionne(moment(moisSelectionne).subtract(1, 'month'));
  };

  // ▶ FONCTION MOIS SUIVANT
  const moisSuivant = () => {
    setMoisSelectionne(moment(moisSelectionne).add(1, 'month'));
  };

  // 🎯 EXTRACTION DU FILTRE DYNAMIQUE
  const filtreMM = moisSelectionne.format('MM');
  const filtreYYYY = moisSelectionne.format('YYYY');

  // 📊 FRACTIONNEMENT VISUEL
  const transactionsDuMois = transactions.filter(t => {
    const moisTx = moment(t.date).format('MM');
    const anneeTx = moment(t.date).format('YYYY');
    return moisTx === filtreMM && anneeTx === filtreYYYY;
  });

  // 🧮 CALCULS DES COMPTEURS DU MOIS SÉLECTIONNÉ
  const totalCredits = transactionsDuMois
    .filter(t => t.type === 'credit')
    .reduce((sum, t) => sum + t.amount, 0);

  const totalDebits = transactionsDuMois
    .filter(t => t.type === 'debit')
    .reduce((sum, t) => sum + t.amount, 0);

  const soldeGlobal = totalCredits - totalDebits;

  // 📡 ÉCOUTEUR FIRESTORE EN TEMPS RÉEL
  useEffect(() => {
    // 🎯 2. LE DOUBLE VERROU MAGIQUE : Laisse passer si c'est TOI (Super-Admin) OU si son rôle Firestore est 'admin'
    const aAccesAuxFinances = ADMIN_LIST.includes(auth.uid) || userRole === 'admin';

    // On n'active l'écouteur que si l'utilisateur est bien l'admin connecté
    if (!auth.uid || !aAccesAuxFinances) return;

    const db = firebase.firestore();

    const unsubscribe = db.collection('finances')
      .where('userId', '==', auth.uid) // 🚀 LA MAGIE EST ICI : Filtre uniquement tes propres lignes !
      .orderBy('date', 'desc')
      .onSnapshot((snapshot) => {
        const list = [];
        snapshot.forEach((doc) => list.push({ id: doc.id, ...doc.data() }));
        setTransactions(list);
      }, (err) => console.error("Erreur finances :", err));

    return () => unsubscribe();
  }, [auth.uid, userRole]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.label || !form.amount) return;

    // 🚀 Lancement de l'action Redux
    addTransaction(form);

    setForm({ label: '', amount: '', type: 'debit', category: 'Aucune catégorie', date: moment().format('YYYY-MM-DD') });
  };

  const handleOpenEdit = (transaction) => {
    setCurrentTransaction(transaction);
    setShowEditModal(true);
  };

  // 🔍 Détecte en temps réel si l'utilisateur a changé quelque chose
  const aDesModifications = () => {
    if (!currentTransaction) return false;

    // Récupère l'élément d'origine dans la liste générale
    const origine = transactions.find(t => t.id === currentTransaction.id);
    if (!origine) return false;

    // Renvoie TRUE dès qu'un champ est différent, sinon FALSE
    return (
      origine.label !== currentTransaction.label ||
      Number(origine.amount) !== Number(currentTransaction.amount) ||
      origine.type !== currentTransaction.type ||
      origine.category !== currentTransaction.category ||
      origine.date !== currentTransaction.date
    );
  };


  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!currentTransaction.label || !currentTransaction.amount) return;

    // 🚀 1. RÉCUPÉRATION DE LA VERSION D'ORIGINE AVANT MODIFICATION
    const transactionOrigine = transactions.find(t => t.id === currentTransaction.id);

    // 🚀 2. VERROU DE SÉCURITÉ : Comparaison stricte de chaque champ
    if (
      transactionOrigine &&
      transactionOrigine.label === currentTransaction.label.trim() &&
      Number(transactionOrigine.amount) === Number(currentTransaction.amount) &&
      transactionOrigine.type === currentTransaction.type &&
      transactionOrigine.category === currentTransaction.category &&
      transactionOrigine.date === currentTransaction.date
    ) {
      // Si rien n'a changé, on ferme sagement la modale sans déclencher Firebase ni Redux !
      setShowEditModal(false);
      return;
    }

    editTransaction(currentTransaction.id, {
      label: currentTransaction.label,
      amount: currentTransaction.amount,
      type: currentTransaction.type,
      category: currentTransaction.category,
      date: currentTransaction.date
    });

    setShowEditModal(false);
  };

  // 🔍 Déclenche l'ouverture de la modale au clic sur la poubelle
  const handleOpenDelete = (transaction) => {
    setTransactionToDelete(transaction);
    setShowDeleteModal(true);
  };

  // 🗑️ Confirme la suppression définitive auprès de Firebase
  const handleConfirmDelete = () => {
    if (transactionToDelete) {
      deleteTransaction(transactionToDelete.id);
    }
    setShowDeleteModal(false);
    setTransactionToDelete(null);
  };


  return (
    <Container fluid className="py-3 min-vh-100 text-light">
      <div className="d-flex flex-column flex-md-row">
        {/* 🚀 1. LA BARRE LATÉRALE RESTE FIXE ICI */}
        <SidebarAdmin />

        {/* 🚀 2. LE CONTENEUR WRAPPER QUI CORRIGE L'ALIGNEMENT DES PAGES */}
        <div className="admin-main-wrapper w-100" style={{ marginBottom: '80px' }}>

          <Container className="py-2 bg-profil min-vh-100">

            {/* 🚀 BARRE DE NAVIGATION MENSUELLE DYNAMIQUE */}
            <div className="d-flex align-items-center justify-content-center gap-3 mb-2 select-none">
              {/* Titre centralisé (ex: "octobre 2026") */}
              <h2 className="text-warning fw-semibold text-capitalize mb-2 text-center">
                📊 Mon Compte Personnel
				      </h2>
            </div>

            <div
              className="d-flex align-items-center align-content-center justify-content-between bg-dark border border-2 border-warning rounded-3 w-100 mx-auto px-4 py-2 mb-5"
              style={{
                maxWidth: '380px',
                boxShadow: '0 0 15px rgba(255, 193, 7, 0.15)', // Légère lueur jaune autour du cadre
                borderWidth: '2px' // Même épaisseur que tes boutons
              }}
            >
              {/* Bouton Mois Précédent */}
              <Button
                variant="warning"
                size="sm"
                onClick={moisPrecedent}
                className="d-flex align-items-center justify-content-center select-none"
                style={{ fontSize: '15px', width: '22px', height: '22px', padding: '0', lineHeight: '1' }}
              >
                ◀
  				    </Button>
              <h2 className="text-light fw-semibold text-capitalize mb-0 text-center flex-grow-1" style={{ fontSize: '1.35rem', letterSpacing: '0.5px' }}>
                {moisSelectionne.format('MMMM YYYY')}
              </h2>
              {/* Bouton Mois Suivant */}
              <Button
                variant="warning"
                size="sm"
                onClick={moisSuivant}
                className="d-flex align-items-center justify-content-center select-none"
                style={{ fontSize: '15px', width: '22px', height: '22px', padding: '0', lineHeight: '1' }}
              >
                ▶
  				    </Button>
            </div>

            {/* 💳 COIN COMPTEURS / BADGES DE SOLDE */}
            <Row className="gy-4 mb-4 text-center align-items-stretch">
              <Col xs={12} lg={7}>
                <Row className="gy-3 h-100 align-content-center">
                  {/* 🟢 CARTE DES CRÉDITS (ENTRÉES) */}
                  <Col xs={12} md={4} className="mb-3">
                    <Card className="bg-dark border-2 border-light border-secondary border-opacity-50 shadow-sm justify-content-center p-3">
                      <h6 className="text-white-50 text-uppercase small fw-semibold tracking-wider mb-2">Crédits</h6>
                      <h4 className="text-success fw-semibold mb-0">+{totalCredits.toFixed(2)} €</h4>
                    </Card>
                  </Col>
                  {/* 🔴 CARTE DES DÉBITS (SORTIES) */}
                  <Col xs={12} md={4} className="mb-3">
                    <Card className="bg-dark border-2 border-light border-secondary border-opacity-50 shadow-sm justify-content-center p-3">
                      <h6 className="text-white-50 text-uppercase small fw-semibold tracking-wider mb-2">Débits</h6>
                      <h4 className="text-danger fw-semibold mb-0">-{totalDebits.toFixed(2)} €</h4>
                    </Card>
                  </Col>
                  {/* 🟡 CARTE DU SOLDE GLOBAL */}
                  <Col xs={12} md={4} className="mb-3">
                    <Card className={`border-2 border-light border-secondary border-opacity-50 shadow-sm justify-content-center p-3 ${soldeGlobal >= 0 ? 'bg-success text-white border-success' : 'bg-danger text-white border-danger'}`}>
                      <h6 className="text-white-50 text-uppercase small fw-semibold tracking-wider mb-2">Solde</h6>
                      <h4 className="text-white fw-semibold mb-0">{soldeGlobal.toFixed(2)} €</h4>
                    </Card>
                  </Col>
                </Row>
              </Col>

              <Col xs={12} lg={5}>
                <FinancesPieChart transactions={transactions} />
              </Col>
            </Row>

            <Row>
              {/* 🛠️ FORMULAIRE D'AJOUT RAPIDE */}
              <Col lg={4} className="mb-4">
                <Card className="bg-dark border-secondary p-3">
                  <h5 className="text-warning mb-3"> Ajouter une opération</h5>
                  <Form onSubmit={handleSubmit}>

                    {/* 1. Libellé / Nom de l'opération */}
                    <Form.Group className="mb-3">
                      <Form.Label className="small">Nom de l'opération</Form.Label>
                      <Form.Control type="text" required value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="Ex: Loyer, Salaire..." className="bg-secondary text-white border-0" />
                    </Form.Group>

                    {/* 2. Montant (€) */}
                    <Form.Group className="mb-3">
                      <Form.Label className="small">Montant (€)</Form.Label>
                      <Form.Control type="number" step="0.01" required value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0.00" className="bg-secondary text-white border-0" />
                    </Form.Group>

                    {/* 3. Type (Débit ou Crédit) */}
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Type</Form.Label>
                      <Form.Select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="bg-secondary text-white border-0 compte-personnel">
                        <option value="debit">📉 Débit</option>
                        <option value="credit">📈 Crédit</option>
                      </Form.Select>
                    </Form.Group>

                    {/* 4. Catégorie */}
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Catégorie</Form.Label>
                      <Form.Select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="bg-secondary text-white border-0 compte-personnel">
                        <option disabled>Choisir une catégorie</option>
                        <option value="Autres"><span>📦</span> Autres</option>
                        <option value="Administratif"><span>📁</span> Administratif</option>
                        <option value="Alimentation"><span>🍏</span> Alimentation</option>
                        <option value="Assurance voiture"><span>🛡️</span> Assurance voiture</option>
                        <option value="Essence"><span>⛽</span> Essence </option>
                        <option value="Facture"><span>🧾</span> Facture</option>
                        <option value="Google Play"><span>🎮</span> Jeux</option>
                        <option value="Loisirs"><span>🎨</span> Loisirs</option>
                        <option value="Loyer"><span>🏠</span> Loyer</option>
                        <option value="Personnel"><span>👤</span> Personnel</option>
                        <option value="Salaire"><span>💰</span> Salaire</option>
                        <option value="Santé"><span>🩺</span> Santé</option>
                        <option value="Tabac"><span>🚬</span> Tabac </option>
                        <option value="Téléphone mobile"><span>📱</span> Téléphone mobile</option>
                        <option value="Voiture"><span>🚗</span> Voiture</option>
                      </Form.Select>
                    </Form.Group>

                    {/* 5. Date de l'opération */}
                    <Form.Group className="mb-3">
                      <Form.Label className="small">Date</Form.Label>
                      <Form.Control type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="bg-secondary text-white border-0" />
                    </Form.Group>
                    <Button type="submit" variant="warning" className="w-100 fw-semibold">Valider l'opération</Button>
                  </Form>
                </Card>
              </Col>

              {/* 📜 HISTORIQUE DU TABLEAU COMPTABLE */}
              <Col lg={8}>
                <Card className="bg-dark border-secondary p-3">
                  <h5 className="text-warning mb-3">Historique des flux</h5>
                  <div className="table-responsive" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                    <Table variant="dark" hover align="middle" className="mb-0" style={{ cursor: 'pointer' }}>
                      <thead>
                        <tr>
                          <th className="fw-semibold">Date</th>
                          <th className="fw-semibold">Libellé</th>
                          <th className="fw-semibold">Catégorie</th>
                          <th className="fw-semibold">Montant</th>
                          <th className="text-center fw-semibold">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactionsDuMois.map((t) => (
                          <tr key={t.id}>
                            <td className="text-capitalize fw-semibold small">{moment(t.date, 'YYYY-MM-DD').locale('fr').format('dddd D MMMM YYYY')}</td>
                            <td className="fw-semibold">{t.label}</td>
                            <td><Badge bg="secondary" className="small">{t.category}</Badge></td>
                            <td className={t.type === 'credit' ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>
                              {t.type === 'credit' ? '+' : '-'}{t.amount.toFixed(2)} €
                            </td>
                            <td className="text-center">
                              <div className="d-flex justify-content-center gap-2">
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => handleOpenEdit(t)}
                                >
                                  ✏️
    											      </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleOpenDelete(t)}
                                >
                                  🗑️
    											      </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {transactionsDuMois.length === 0 && (
                          <tr><td colSpan="5" className="text-center text-white-50 py-3">Aucune opération ce mois-ci.</td></tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card>
              </Col>
            </Row>

            {/* 🎛️ MODALE D'ÉDITION D'UNE OPÉRATION FINANCIÈRE */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered contentClassName="bg-dark text-light border-secondary">
              <Modal.Header closeButton closeVariant="white" className="border-secondary">
                <Modal.Title className="text-warning fw-semibold">Modifier l'opération</Modal.Title>
              </Modal.Header>

              {currentTransaction && (
                <Form onSubmit={handleEditSubmit}>
                  <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', paddingBottom: '20px' }}>
                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-semibold">Nom de l'opération</Form.Label>
                      <Form.Control
                        type="text"
                        value={currentTransaction.label}
                        onChange={e => setCurrentTransaction({ ...currentTransaction, label: e.target.value })}
                        required
                        className="bg-secondary text-white border-0"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-semibold">Montant (€)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        required
                        value={currentTransaction.amount}
                        onChange={e => setCurrentTransaction({ ...currentTransaction, amount: e.target.value })}
                        className="bg-secondary text-white border-0"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-semibold">Type</Form.Label>
                      <Form.Select
                        value={currentTransaction.type}
                        onChange={e => setCurrentTransaction({ ...currentTransaction, type: e.target.value })}
                        className="bg-secondary text-white border-0 compte-personnel"
                      >
                        <option value="debit">📉 Débit (Dépense)</option>
                        <option value="credit">📈 Crédit (Rentrée)</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-semibold">Catégorie</Form.Label>
                      <Form.Select
                        value={currentTransaction.category}
                        onChange={e => setCurrentTransaction({ ...currentTransaction, category: e.target.value })}
                        className="bg-secondary text-white border-0 compte-personnel"
                      >
                        <option disabled>Choisir une catégorie</option>
                        <option value="Autres"><span>📦</span> Autres</option>
                        <option value="Administratif"><span>📁</span> Administratif</option>
                        <option value="Alimentation"><span>🍏</span> Alimentation</option>
                        <option value="Assurance voiture"><span>🛡️</span> Assurance voiture</option>
                        <option value="Essence"><span>⛽</span> Essence </option>
                        <option value="Facture"><span>🧾</span> Facture</option>
                        <option value="Google Play"><span>🎮</span> Jeux</option>
                        <option value="Loisirs"><span>🎨</span> Loisirs</option>
                        <option value="Loyer"><span>🏠</span> Loyer</option>
                        <option value="Personnel"><span>👤</span> Personnel</option>
                        <option value="Salaire"><span>💰</span> Salaire</option>
                        <option value="Santé"><span>🩺</span> Santé</option>
                        <option value="Tabac"><span>🚬</span> Tabac </option>
                        <option value="Téléphone mobile"><span>📱</span> Téléphone mobile</option>
                        <option value="Voiture"><span>🚗</span> Voiture</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-semibold">Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={currentTransaction.date}
                        onChange={e => setCurrentTransaction({ ...currentTransaction, date: e.target.value })}
                        className="bg-secondary text-white border-0"
                      />
                    </Form.Group>
                  </Modal.Body>

                  <Modal.Footer className="d-flex justify-content-end border-secondary border-1 pt-3">
                    <Button variant="secondary" className="px-3 fw-semibold" onClick={() => setShowEditModal(false)}>Annuler</Button>
                    <Button type="submit" variant="warning" className="px-3 fw-semibold" disabled={!aDesModifications()}>Sauvegarder</Button>
                  </Modal.Footer>
                </Form>
              )}
            </Modal>

            {/* ⚠️ MODALE DE CONFIRMATION DE SUPPRESSION STRICTE */}
            <Modal
              show={showDeleteModal}
              onHide={() => setShowDeleteModal(false)}
              centered
              contentClassName="bg-dark text-light border-danger border-2"
            >
              <Modal.Header closeButton closeVariant="white" className="border-secondary">
                <Modal.Title className="text-danger fw-semibold d-flex align-items-center">
                  Attention
                </Modal.Title>
              </Modal.Header>

              <Modal.Body className="py-4">
                {transactionToDelete && (
                  <div className="text-center">
                    <p className="fs-custom-1-1 mb-3">
                      Voulez-vous supprimer cette opération ?
        			      </p>
                    <div className="p-3 bg-secondary bg-opacity-25 rounded-3 mb-3 border border-secondary text-start">
                      <div className="small text-light mb-1">Détails de la transaction :</div>
                      <div className="fw-semibold text-warning">{transactionToDelete.label}</div>
                      <div className="small">
                        Montant : <span className={transactionToDelete.type === 'credit' ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>
                          {transactionToDelete.type === 'credit' ? '+' : '-'}{transactionToDelete.amount.toFixed(2)} €
            			</span>
                      </div>
                    </div>
                    <p className="text-danger small fw-semibold mb-0">
                      🛑 Cette action est irréversible.
        			      </p>
                  </div>
                )}
              </Modal.Body>

              <Modal.Footer className="d-flex justify-content-end border-secondary border-1 pt-3">
                <Button
                  variant="secondary"
                  className="px-3 fw-semibold"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Annuler
    			      </Button>
                <Button
                  variant="danger"
                  className="px-3 fw-semibold"
                  onClick={handleConfirmDelete}
                >
                  Supprimer
    			      </Button>
              </Modal.Footer>
            </Modal>
          </Container>
        </div>
      </div>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  auth: state.firebase.auth,
  profile: state.firebase.profile
});
export default connect(mapStateToProps, { addTransaction, editTransaction, deleteTransaction })(ComptePersonnel);
